import { Link } from "react-router-dom";

import type { BelegReviewState, BelegRow } from "../api/belege";
import { toNumber } from "../lib/format";
import { reviewStateLabel } from "../lib/reviewStatusLabels";

const STATE_COUNT_KEYS: Record<
  BelegReviewState,
  keyof Pick<
    BelegRow,
    | "review_open_count"
    | "review_complaint_count"
    | "review_credit_expected_count"
    | "review_write_off_count"
    | "review_credit_resolved_count"
  >
> = {
  open: "review_open_count",
  complaint: "review_complaint_count",
  credit_expected: "review_credit_expected_count",
  write_off: "review_write_off_count",
  credit_resolved: "review_credit_resolved_count",
};

const STATE_ORDER: BelegReviewState[] = [
  "open",
  "complaint",
  "credit_expected",
  "write_off",
  "credit_resolved",
];

const STATE_TONES: Record<BelegReviewState, string> = {
  open: "bg-rose-50 text-rose-700",
  complaint: "bg-amber-50 text-amber-800",
  credit_expected: "bg-sky-50 text-sky-700",
  write_off: "bg-slate-100 text-slate-700",
  credit_resolved: "bg-emerald-50 text-emerald-700",
};

export default function BelegReviewStates({
  beleg,
  href,
  from,
  emptyLabel = "Keine Preisprüfung",
}: {
  beleg: BelegRow;
  href?: string;
  from?: string;
  emptyLabel?: string;
}) {
  const entries = STATE_ORDER.flatMap((state) => {
    const count = toNumber(beleg[STATE_COUNT_KEYS[state]]) ?? 0;
    return count > 0 ? [{ state, count }] : [];
  });
  const unspecifiedCount = toNumber(beleg.review_unspecified_count) ?? 0;

  if (entries.length === 0 && unspecifiedCount === 0) {
    return <span className="text-xs text-slate-400">{emptyLabel}</span>;
  }

  return (
    <div
      className="flex flex-wrap gap-1"
      data-testid="beleg-review-states"
      aria-label="Prüfzustände"
    >
      {entries.map(({ state, count }) => (
        <ReviewStateBadge
          key={state}
          label={`${count.toLocaleString("de-AT")} ${reviewStateLabel(state)}`}
          tone={STATE_TONES[state]}
          href={href}
          from={from}
        />
      ))}
      {unspecifiedCount > 0 ? (
        <ReviewStateBadge
          label={`${unspecifiedCount.toLocaleString("de-AT")} Prüfstatus nicht näher angegeben`}
          tone="bg-violet-50 text-violet-700"
          href={href}
          from={from}
        />
      ) : null}
    </div>
  );
}

function ReviewStateBadge({
  label,
  tone,
  href,
  from,
}: {
  label: string;
  tone: string;
  href?: string;
  from?: string;
}) {
  const className = `inline-flex rounded px-2 py-0.5 text-xs font-medium leading-tight ${tone}`;
  return href ? (
    <Link
      to={href}
      state={from ? { from } : undefined}
      className={`${className} hover:underline`}
    >
      {label}
    </Link>
  ) : (
    <span className={className}>{label}</span>
  );
}
